import React, { useState } from "react";
import {
  TextField,
  Grid,
  Button,
  Typography,
  Autocomplete,
  Switch,
  FormControlLabel,
  Box,
  IconButton,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import "./basic-matching-information.css";
import AddIcon from "@mui/icons-material/Add";
import ReusableDialog from "../../../../../common/dialog/dialog";
import DeleteIcon from "@mui/icons-material/Delete";

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  homeName: Yup.string().required("Name of Children's Home is required"),
  managerName: Yup.string().required("Name of Registered Manager is required"),
  initials: Yup.string().required("Initials are required"),
  gender: Yup.string().required("Gender is required"),
  dob: Yup.string().required("Date of Birth is required"),
  age: Yup.number()
    .typeError("Age must be a number")
    .required("Age is required")
    .min(0, "Age cannot be less than 0")
    .max(120, "Age cannot be more than 120"),
  matchingDate: Yup.string().required("Matching Date is required"),
});

const genders = ["Others", "Male", "Female"]; // Gender options
const homeNames = ["Home1", "Home2", "Home3"]; // Home name options

const BasicMatchingInformation = ({ updateFormData, nextStep }) => {
  const [inHome, setInHome] = useState(false); // To manage the toggle
  const [open, setOpen] = useState(false); // For dialog
  const [dialogFormData, setDialogFormData] = useState({
    initials: "",
    gender: "",
    dob: "",
    age: "",
  }); // Dialog form data

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setDialogFormData({ initials: "", gender: "", dob: "", age: "" }); // Reset dialog form on close
  };

  const handleDialogInputChange = (e) => {
    const { name, value } = e.target;
    setDialogFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDialogSelectChange = (field, value) => {
    setDialogFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Formik
      initialValues={{
        homeName: "",
        managerName: "",
        initials: "",
        gender: "",
        dob: "",
        age: "",
        matchingDate: "",
        inHome: false,
        peopleInHome: [], // Array to handle people in the home
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        updateFormData({ basicmatchingInfo: values });
        nextStep(); // Move to next form
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, errors, touched }) => (
        <Form onSubmit={handleSubmit} className='basic-matching-form'>
          <Typography variant='h6' sx={{ fontWeight: "bold" }} align='left'>
            Post Visit Matching Assessment
          </Typography>
          <Typography variant='body2' gutterBottom>
            Please provide the basic information related to the potential admission and the children’s home.
          </Typography>

          {/* Home and Manager Details */}
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} lg={6}>
              <Autocomplete
                id='homeName'
                options={homeNames}
                value={values.homeName}
                onChange={(event, newValue) => setFieldValue("homeName", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontWeight: "bold" }} align='left'>
                      Name of Children's Home
                    </Typography>
                    <TextField
                      {...params}
                      placeholder="Select Children's Home"
                      error={Boolean(touched.homeName && errors.homeName)}
                      helperText={touched.homeName && errors.homeName ? errors.homeName : ""}
                    />
                  </>
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Typography sx={{ fontWeight: "bold" }} align='left'>
                Name of Registered Manager
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter Name of Registered Manager'
                name='managerName'
                value={values.managerName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.managerName && Boolean(errors.managerName)}
                helperText={touched.managerName && errors.managerName}
              />
            </Grid>
          </Grid>

          {/* Potential Admission */}
          <Typography variant='h6' gutterBottom mt={2}>
            Potential Admission
          </Typography>

          <Grid container mt={1} spacing={2}>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: "bold" }} align='left'>
                Initials
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter Initials'
                name='initials'
                value={values.initials}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.initials && Boolean(errors.initials)}
                helperText={touched.initials && errors.initials}
              />
            </Grid>
          </Grid>
          <Grid container mt={1} spacing={2}>
            <Grid item xs={3}>
              <Autocomplete
                id='gender'
                options={genders}
                value={values.gender}
                onChange={(event, newValue) => setFieldValue("gender", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontWeight: "bold" }} align='left'>
                      Gender
                    </Typography>
                    <TextField
                      {...params}
                      placeholder='Gender'
                      error={Boolean(touched.gender && errors.gender)}
                      helperText={touched.gender && errors.gender ? errors.gender : ""}
                    />
                  </>
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={3}>
              <Typography sx={{ fontWeight: "bold" }} align='left'>
                Date of Birth
              </Typography>
              <TextField
                fullWidth
                name='dob'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={values.dob}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dob && Boolean(errors.dob)}
                helperText={touched.dob && errors.dob}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: "bold" }} align='left'>
                Age at Time of Admission
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter Age at time of admission'
                name='age'
                type='number'
                value={values.age}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.age && Boolean(errors.age)}
                helperText={touched.age && errors.age}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography sx={{ fontWeight: "bold" }} align='left'>
                Date of Matching Assessment
              </Typography>
              <TextField
                fullWidth
                name='matchingDate'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={values.matchingDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.matchingDate && Boolean(errors.matchingDate)}
                helperText={touched.matchingDate && errors.matchingDate}
              />
            </Grid>
          </Grid>

          {/* Toggle for people already in the home */}
          <Grid container spacing={2} mt={3}>
            <Grid item xs={6}>
              <Typography variant='body2' gutterBottom>
                Young Person(s) already in the home
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={inHome}
                    onChange={(event) => {
                      setFieldValue("inHome", event.target.checked);
                      setInHome(!inHome);
                    }}
                    color='primary'
                  />
                }
              />
            </Grid>
          </Grid>

          {/* Table for people already in the home */}
          {inHome && (
            <FieldArray name='peopleInHome'>
              {({ push, remove }) => (
                <>
                  <table className='people-table'>
                    <thead>
                      <tr>
                        <th>Initials</th>
                        <th>Gender</th>
                        <th>Date of Birth</th>
                        <th>Age</th>
                        <th>Action</th> {/* New Action column for the delete button */}
                      </tr>
                    </thead>
                    <tbody>
                      {values.peopleInHome.map((person, index) => (
                        <tr key={index}>
                          <td>
                            <TextField
                              fullWidth
                              name={`peopleInHome.${index}.initials`}
                              value={person.initials}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <TextField
                              fullWidth
                              value={person.gender}
                              name={`peopleInHome.${index}.gender`}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <TextField
                              fullWidth
                              type='date'
                              InputLabelProps={{ shrink: true }}
                              name={`peopleInHome.${index}.dob`}
                              value={person.dob}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <TextField
                              fullWidth
                              name={`peopleInHome.${index}.age`}
                              value={person.age}
                              type='number'
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <IconButton onClick={() => remove(index)} color='primary'>
                              <DeleteIcon />
                            </IconButton>
                          </td>{" "}
                          {/* Delete icon to remove specific row */}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Add person button */}
                  <Grid container justifyContent='space-between' alignItems='center' mt={2}>
                    <Grid item></Grid>
                    <Grid item>
                      <Button onClick={handleOpenDialog} color='primary'>
                        <AddIcon /> Add Another Person
                      </Button>
                    </Grid>
                  </Grid>
                  {/* Dialog for Adding People */}
                  <ReusableDialog
                    isOpen={open}
                    onClose={handleCloseDialog}
                    title='Add a young person Details ?'
                    contentText='Add the relevant details to the fields to add another person to the involved parties.'
                    onSubmit={() => {
                      push(dialogFormData); // Push the dialog form data into FieldArray
                      handleCloseDialog(); // Close dialog after submission
                    }}
                    submitText='Add'
                    cancelText='Cancel'
                  >
                    <Box mt={2}>
                      {/* Custom TextField passed as children */}
                      <Grid container mt={1}>
                        <Grid item xs={8}>
                          <Typography sx={{ fontWeight: "bold" }} align='left'>
                            Initials
                          </Typography>
                          <TextField
                            fullWidth
                            placeholder='Enter Initials'
                            name='initials'
                            value={dialogFormData.initials}
                            onChange={handleDialogInputChange}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={4}>
                          <Autocomplete
                            id='gender'
                            options={genders}
                            value={dialogFormData.gender}
                            onChange={(event, newValue) => handleDialogSelectChange("gender", newValue)}
                            renderInput={(params) => (
                              <>
                                <Typography sx={{ fontWeight: "bold" }} align='left'>
                                  Gender
                                </Typography>
                                <TextField {...params} placeholder='Gender' />
                              </>
                            )}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={4}>
                          <Typography sx={{ fontWeight: "bold" }} align='left'>
                            Date of Birth
                          </Typography>
                          <TextField
                            fullWidth
                            name='dob'
                            type='date'
                            InputLabelProps={{ shrink: true }}
                            value={dialogFormData.dob}
                            onChange={handleDialogInputChange}
                          />
                        </Grid>
                      </Grid>

                      <Grid container mt={2}>
                        <Grid item xs={8}>
                          <Typography sx={{ fontWeight: "bold" }} align='left'>
                            Age at Time of Admission
                          </Typography>
                          <TextField
                            fullWidth
                            placeholder='Enter Age'
                            name='age'
                            type='number'
                            value={dialogFormData.age}
                            onChange={handleDialogInputChange}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </ReusableDialog>
                </>
              )}
            </FieldArray>
          )}

          <Grid item xs={12} mt={1}>
            <div className='submit-button-container'>
              <Button type='submit' variant='contained' color='primary' className='submit-button'>
                Next
              </Button>
            </div>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default BasicMatchingInformation;
